import React from 'react';
import {
    ActivityIndicator,
    Platform,
    TouchableOpacity,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { Link, type Href, usePathname, useRouter } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { buildDashboardPath } from '../../lib/dashboardRoutes';
import { ProjectSettingsButton } from '../ui/Button';
import type { RssUpgradeAction } from '../../lib/uiEntitlementFeatures';
import { Trans } from '@lingui/react/macro';
import {
    type LocalizedMessage,
    useOptionalResolvedMessage,
    useResolvedMessage,
} from '../../lib/i18n/messages';

interface ProjectFeedToolbarProps {
    digestId: string;
    projectName: string;
    copied: boolean;
    isCopying: boolean;
    atomFeedError: LocalizedMessage | null;
    atomFeedNotice?: LocalizedMessage | null;
    atomFeedUpgradeAction: RssUpgradeAction;
    atomFeedTrialHref?: Href;
    compact: boolean;
    onCopyAtomFeedUrl: () => void;
}

export default function ProjectFeedToolbar({
    digestId,
    projectName,
    copied,
    isCopying,
    atomFeedError,
    atomFeedNotice,
    atomFeedUpgradeAction,
    atomFeedTrialHref,
    compact,
    onCopyAtomFeedUrl,
}: ProjectFeedToolbarProps) {
    const { fontsLoaded } = useFontLoading();
    const resolvedAtomFeedError = useOptionalResolvedMessage(atomFeedError);
    const resolvedAtomFeedNotice = useOptionalResolvedMessage(atomFeedNotice);
    const resolvedUpgradeActionLabel = useResolvedMessage(atomFeedUpgradeAction.label);
    const pathname = usePathname();
    const router = useRouter();

    return (
        <View style={styles.toolbar} testID="project-feed-toolbar">
            <View style={[styles.topRow, compact && styles.topRowCompact]}>
                <View style={styles.titleBlock}>
                    <Text style={[styles.projectTitle, fontsLoaded && interSemiBold]}>
                        {projectName}
                    </Text>
                </View>

                <View style={[styles.actionRow, compact && styles.actionRowCompact]}>
                    <Link href={buildDashboardPath(pathname, `/${digestId}/settings`)} asChild>
                        <ProjectSettingsButton
                            variant="primary"
                            style={styles.primaryActionButton}
                            textStyle={styles.primaryActionText}
                            iconSize={12}
                            activeOpacity={0.75}
                        />
                    </Link>

                    <TouchableOpacity
                        style={[styles.secondaryActionButton, copied && styles.secondaryActionButtonActive]}
                        activeOpacity={0.75}
                        onPress={onCopyAtomFeedUrl}
                        disabled={isCopying}
                    >
                        {isCopying ? (
                            <ActivityIndicator size="small" color={colors.primaryGreen} />
                        ) : (
                            <FontAwesome6 name={copied ? 'check' : 'rss'} size={12} color={colors.text700} />
                        )}
                        <Text
                            style={[
                                styles.secondaryActionText,
                                copied && styles.secondaryActionTextActive,
                                fontsLoaded && interSemiBold,
                            ]}
                        >
                            {isCopying ? <Trans id="feed.projectfeedtoolbar.generating" comment="Conditional UI copy in components/feed/ProjectFeedToolbar.tsx">Generating...</Trans> : copied ? <Trans id="feed.projectfeedtoolbar.copied" comment="Conditional UI copy in components/feed/ProjectFeedToolbar.tsx">Copied</Trans> : <Trans id="feed.projectfeedtoolbar.copyRss" comment="Conditional UI copy in components/feed/ProjectFeedToolbar.tsx">Copy RSS</Trans>}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>

            {resolvedAtomFeedNotice ? (
                atomFeedUpgradeAction.kind === 'available' ? (
                    <Text style={[styles.noticeText, fontsLoaded && interRegular]}>
                        {resolvedAtomFeedNotice}
                    </Text>
                ) : atomFeedUpgradeAction.kind === 'trial' ? (
                    <Text style={[styles.noticeText, fontsLoaded && interRegular]}><Trans id="feed.projectfeedtoolbar.startA14DayFreeTrialToCopyRssAtomFeedsFor" comment="UI copy in components/feed/ProjectFeedToolbar.tsx">
                        <Text
                            style={[styles.noticeLink, fontsLoaded && interSemiBold]}
                            onPress={() => router.push(atomFeedTrialHref ?? atomFeedUpgradeAction.path)}
                            accessibilityRole="link"
                        >
                            Start a 14-day free trial
                        </Text>
                        {' '}to copy RSS/Atom feeds for this topic. No obligation to buy.
                    </Trans></Text>
                ) : atomFeedUpgradeAction.kind === 'legacy_offer' ? (
                    <Text style={[styles.noticeText, fontsLoaded && interRegular]}><Trans id="feed.projectfeedtoolbar.yourAccountIsNowOnTheFreePlanToRestore" comment="UI copy in components/feed/ProjectFeedToolbar.tsx">
                        Your account is now on the Free plan.{' '}
                        <Text
                            style={[styles.noticeLink, fontsLoaded && interSemiBold]}
                            onPress={() => router.push(atomFeedUpgradeAction.path)}
                            accessibilityRole="link"
                        >
                            {resolvedUpgradeActionLabel}
                        </Text>
                        {' '}to restore RSS/Atom access.
                    </Trans></Text>
                ) : (
                    <Text style={[styles.noticeText, fontsLoaded && interRegular]}><Trans id="feed.projectfeedtoolbar.rssAtomFeedsAreIncludedWithPaidPlansViewPlansWhen" comment="UI copy in components/feed/ProjectFeedToolbar.tsx">
                        RSS/Atom feeds are included with paid plans.{' '}
                        <Text
                            style={[styles.noticeLink, fontsLoaded && interSemiBold]}
                            onPress={() => router.push(atomFeedUpgradeAction.path)}
                            accessibilityRole="link"
                        >
                            View plans
                        </Text>
                        {' '}when you want to read Paperzilla in your feed reader.
                    </Trans></Text>
                )
            ) : resolvedAtomFeedError ? (
                <Text style={[styles.errorText, fontsLoaded && interRegular]}>
                    {resolvedAtomFeedError}
                </Text>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    toolbar: {
        width: '100%',
        gap: 8,
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    topRowCompact: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    titleBlock: {
        justifyContent: 'center',
    },
    projectTitle: {
        fontSize: 28,
        lineHeight: 32,
        color: colors.text900,
    },
    actionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
    },
    actionRowCompact: {
        width: '100%',
    },
    primaryActionButton: {
        borderRadius: 999,
        paddingHorizontal: 14,
        paddingVertical: 8,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    primaryActionText: {
        fontSize: 13,
    },
    secondaryActionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        borderRadius: 999,
        paddingHorizontal: 14,
        paddingVertical: 8,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#d9dde5',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    secondaryActionButtonActive: {
        backgroundColor: '#f4f8f0',
        borderColor: '#c3d3bc',
    },
    secondaryActionText: {
        fontSize: 13,
        color: colors.text700,
    },
    secondaryActionTextActive: {
        color: colors.primaryGreenDark,
    },
    errorText: {
        fontSize: 13,
        color: colors.errorRed,
    },
    noticeText: {
        fontSize: 13,
        color: colors.primaryGreenDark,
    },
    noticeLink: {
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
});
